// ============================================================================
// VOICE SELECTOR
// Shared picker for the managed TTS voice list, with an audio preview button.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Play, Square } from 'lucide-react';
import { toast } from 'sonner';
import { TtsVoice, TtsVoiceUsage, apiErrorMessage, listVoicesCached, ttsVoicesService } from '../services/ttsVoicesService';

export function useVoicePreview() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const requestRef = useRef(0);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const stop = useCallback(() => {
    requestRef.current += 1;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setPlayingId(null);
    setLoadingId(null);
  }, []);

  const toggle = useCallback(async (voiceId: string) => {
    const wasActive = playingId === voiceId || loadingId === voiceId;
    stop();
    if (wasActive) return;

    const requestId = requestRef.current;
    setLoadingId(voiceId);
    try {
      const url = await ttsVoicesService.preview(voiceId);
      if (requestId !== requestRef.current) return;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        if (audioRef.current === audio) {
          audioRef.current = null;
          setPlayingId(null);
        }
      };
      await audio.play();
      if (requestId !== requestRef.current) return;
      setPlayingId(voiceId);
    } catch (err: any) {
      if (requestId !== requestRef.current) return;
      audioRef.current = null;
      toast.error(apiErrorMessage(err, 'Could not play voice preview'));
    } finally {
      if (requestId === requestRef.current) setLoadingId(null);
    }
  }, [playingId, loadingId, stop]);

  useEffect(() => stop, [stop]);

  return { playingId, loadingId, toggle, stop };
}

interface VoicePreviewButtonProps {
  voiceId: string;
  playingId: string | null;
  loadingId: string | null;
  onToggle: (voiceId: string) => void;
  disabled?: boolean;
  size?: number;
}

export const VoicePreviewButton: React.FC<VoicePreviewButtonProps> = ({ voiceId, playingId, loadingId, onToggle, disabled, size = 34 }) => {
  const loading = loadingId === voiceId;
  const playing = playingId === voiceId;
  const label = playing || loading ? 'Stop preview' : 'Play voice preview';
  return (
    <button
      type="button"
      onClick={() => onToggle(voiceId)}
      disabled={disabled}
      title={label}
      aria-label={label}
      style={{
        width: `${size}px`, height: `${size}px`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '8px', border: `1px solid ${playing ? '#00c7c4' : '#e2e8f0'}`, background: playing ? '#e6fafa' : 'white',
        color: playing || loading ? '#00a3a0' : '#475569', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1
      }}
    >
      {loading
        ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
        : playing ? <Square size={12} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
    </button>
  );
};

interface VoiceSelectorProps {
  value: string;
  onChange: (voiceId: string) => void;
  usage?: TtsVoiceUsage;
  disabled?: boolean;
}

const VoiceSelector: React.FC<VoiceSelectorProps> = ({ value, onChange, usage = 'avatar', disabled }) => {
  const [voices, setVoices] = useState<TtsVoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const preview = useVoicePreview();
  const { stop } = preview;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    listVoicesCached(usage)
      .then(list => { if (!cancelled) setVoices(list); })
      .catch(err => { if (!cancelled) setLoadError(apiErrorMessage(err, 'Failed to load voices')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [usage]);

  const sorted = useMemo(
    () => [...voices].sort((a, b) => (a.accent || '').localeCompare(b.accent || '') || a.label.localeCompare(b.label)),
    [voices]
  );

  const groups = useMemo(() => {
    const map = new Map<string, TtsVoice[]>();
    for (const v of sorted) {
      const key = v.accent || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(v);
    }
    return [...map.entries()];
  }, [sorted]);

  const isListed = voices.some(v => v.voiceId === value);
  const isUnlisted = !loading && !!value && !isListed;

  const handleChange = (next: string) => {
    stop();
    onChange(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <select
          value={loading ? '' : value}
          onChange={e => handleChange(e.target.value)}
          disabled={disabled || loading}
          style={{ flex: 1, minWidth: 0, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#0f172a', background: 'white' }}
        >
          {loading && <option value="">Loading voices…</option>}
          {!loading && !value && <option value="" disabled>Select a voice…</option>}
          {isUnlisted && <option value={value}>Unlisted (current)</option>}
          {!loading && groups.map(([accent, list]) => (
            <optgroup key={accent} label={accent}>
              {list.map(v => (
                <option key={v.voiceId} value={v.voiceId}>{v.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
        {isListed && (
          <VoicePreviewButton
            voiceId={value}
            playingId={preview.playingId}
            loadingId={preview.loadingId}
            onToggle={preview.toggle}
            disabled={disabled}
          />
        )}
      </div>
      {isUnlisted && (
        <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
          This avatar uses a voice that is not in the managed list (<code>{value}</code>). It is kept unless you pick another.
        </p>
      )}
      {loadError && (
        <p style={{ margin: 0, fontSize: '12px', color: '#ef4444' }}>{loadError}</p>
      )}
    </div>
  );
};

export default VoiceSelector;
